import { differenceInMonths, parseISO } from 'date-fns';
import { getSwitchingQuotes, postAdditionalUserInfo } from 'actions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { GET_SWITCHING_QUOTES_SUCCESS, POST_ADDITIONAL_USER_INFO_SUCCESS, } from 'actions/types';
import Amplitude from 'utils/amplitude';
import Modal from 'utils/packages/Modal';
import { loanTypeReasonValue } from './models';
import { selectMonevoAdditionalInfoChanges, selectMonevoSelectedAccount, } from './selectors';
import { selectMonevoStepContext } from './steps';
/**
 * Statuses that mean a saved detail is missing or no longer usable. Everything
 * else — timeouts, server errors — keeps the ordinary error surface rather
 * than pushing the user through a dozen screens over a blip.
 */
const REUSE_FALLBACK_STATUSES = [400, 422];
/**
 * The one place a Monevo quote is asked for, shared by the funnel's last
 * screen, the loan amount screen in reuse mode and the credit-card path. It
 * owns the user-info write, the payload, the quote-completed timestamps and
 * the submission event; the caller decides only what happens to navigation
 * afterwards, which genuinely differs between the three.
 */
export const submitMonevoQuote = async (dispatch, getState) => {
    /**
     * The backend reads several fields off the user record rather than the
     * request body, so this write has to land before the quote is asked for.
     */
    const changes = selectMonevoAdditionalInfoChanges(getState());
    if (Object.keys(changes).length) {
        const saved = await dispatch(postAdditionalUserInfo(changes));
        if (saved?.type !== POST_ADDITIONAL_USER_INFO_SUCCESS) {
            return { status: 'error' };
        }
    }
    const { onboarding, user, primaryAddress, previousAddress } = selectMonevoStepContext(getState());
    const account = selectMonevoSelectedAccount(getState());
    const isReusingDetails = !!onboarding.reuseDetails;
    const body = {
        partner: 'MONEVO_UK',
        type: onboarding.type === 'CREDIT_CARD' ? 'CREDIT_CARD' : 'LOAN',
        mainBankAccountId: account?.id,
        data: {
            current_address_id: primaryAddress?.id,
            prev_address_id: previousAddress?.id,
            loan_amount: onboarding.type === 'CREDIT_CARD' ? undefined : onboarding.loanAmount,
            loan_purpose: onboarding.loanPurpose,
            loan_term: onboarding.loanTerm,
            income_source: onboarding.employmentStatus?.value,
            months_at_employer: onboarding.employmentStartDate
                ? differenceInMonths(new Date(), parseISO(onboarding.employmentStartDate))
                : undefined,
            gross_annual_income: onboarding.income,
            rental_mortgage_payments: onboarding.rentalMortgagePayments == null
                ? undefined
                : String(onboarding.rentalMortgagePayments),
            terms_consent: 1,
            email: user.email,
            mobile_phone: user.phoneNumber,
            time_at_bank: onboarding.timeAtBank || undefined,
        },
    };
    const response = await dispatch(getSwitchingQuotes(body, isReusingDetails ? REUSE_FALLBACK_STATUSES : undefined));
    if (response?.type !== GET_SWITCHING_QUOTES_SUCCESS) {
        const { status } = response?.payload ?? {};
        if (isReusingDetails &&
            status &&
            REUSE_FALLBACK_STATUSES.includes(status)) {
            return { status: 'needsFunnel' };
        }
        return { status: 'error' };
    }
    const url = response.payload?.webview?.url;
    if (!url) {
        Modal.showError('This is awkward, please try again.');
        return { status: 'error' };
    }
    /**
     * What retires the credit-report offer cards. Written on every path — a
     * reuse path that skipped it would leave those cards nagging users who have
     * just got a quote.
     */
    if (body.data.loan_purpose === loanTypeReasonValue.DEBT) {
        dispatch(setDeviceSettingsKey('monevoDebtQuoteCompletedAt', Date.now()));
    }
    else if (body.data.loan_purpose === loanTypeReasonValue.CREDIT_CARD) {
        dispatch(setDeviceSettingsKey('monevoCreditCardQuoteCompletedAt', Date.now()));
    }
    Amplitude.logEvent('Switching Quotes Screen', {
        source: 'monevo',
        reusedDetails: isReusingDetails,
        previousQuoteAgeDays: onboarding.previousQuoteAgeDays,
    });
    return { status: 'success', url };
};
